import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  ADS_CAMPAIGNS_STATISTICS_SLIDE_MENU_METRICS,
  COLUMN_NAME_PLATFORM,
  LOADING_CONFIGURATION_METRICS,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import MetaCampaignRow from "./Meta/MetaCampaignRow";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../services/adsCampaigns";
import { GOOGLE, META, TIKTOK, LINKEDIN } from "../../../../utils/constants";
import TableContainer from "../../../../components/Tables/TableContainer";
import TableCell from "../../../BillingAndPayments/components/TableCell";
import GoogleCampaignRow from "./Google/GoogleCampaignRow";
import TiktokCampaignRow from "./Tiktok/TiktokCampaignRow";
import LinkedInCampaignRow from "./LinkedIn/LinkedInCampaignRow";
import { useState, useEffect } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  DEFAULT_METRIC_COLUMN_WIDTH,
  METRIC_COLUMN_MIN_WIDTH,
  expanderColumnSx,
  metricColumnSx,
} from "./columnWidths";
import MetricsVisibilitySelector from "../../../../components/Common/MetricsVisibilitySelector";
import {
  collectMultipleConversionKeys,
  getActionMetricKeys,
  getCampaignMetricLabel,
  getGeneralMetricKeys,
  getMetricsToShow,
} from "../../../NewAdsCampaign/components/Common/campaignLevelMetrics";
import Stack from "@mui/material/Stack";
import { Avatar } from "@mui/material";
import { logoPlatform } from "../../../../utils/string";

const COMPONENT_ROWS = {
  [META]: MetaCampaignRow,
  [GOOGLE]: GoogleCampaignRow,
  [TIKTOK]: TiktokCampaignRow,
  [LINKEDIN]: LinkedInCampaignRow,
};

const TableStatistics = () => {
  const { t } = useTranslationApp();
  const [sortConfigs, setSortConfigs] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({
    [GOOGLE]: {},
    [META]: {},
    [TIKTOK]: {},
    [LINKEDIN]: {},
  });
  const [metricsInitialized, setMetricsInitialized] = useState({
    [GOOGLE]: false,
    [META]: false,
    [TIKTOK]: false,
    [LINKEDIN]: false,
  });
  const [columnWidths, setColumnWidths] = useState({
    [GOOGLE]: {},
    [META]: {},
    [TIKTOK]: {},
    [LINKEDIN]: {},
  });

  const { campaign } = useFormContext();
  const { platforms, statistics } = getFromStatisticsAdsCampaign(campaign);

  // Same default as the campaign view: general metrics visible, the rest hidden
  // until the saved configuration replaces this state.
  useEffect(() => {
    const generalMetricKeys = new Set(getGeneralMetricKeys());
    const initial = {};
    [GOOGLE, META, TIKTOK, LINKEDIN].forEach((platform) => {
      initial[platform] = {};
      getMetricsToShow({
        platform,
        actionMetricsKeys: getActionMetricKeys(platform),
      }).forEach((metric) => {
        initial[platform][metric] = generalMetricKeys.has(metric);
      });
    });
    setColumnVisibility(initial);
  }, []);

  const handleSort = (platform, key) => {
    setSortConfigs((prevConfigs) => ({
      ...prevConfigs,
      [platform]: {
        key,
        direction:
          prevConfigs[platform]?.key === key &&
          prevConfigs[platform]?.direction === "asc"
            ? "desc"
            : "asc",
      },
    }));
  };

  const getColumnWidth = (platform, key) =>
    columnWidths[platform]?.[key] ?? DEFAULT_METRIC_COLUMN_WIDTH;

  const setColumnWidth = (platform, key, width) => {
    setColumnWidths((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [key]: Math.max(METRIC_COLUMN_MIN_WIDTH, Math.round(width)),
      },
    }));
  };

  const SortIcon = ({ platform, columnKey }) => {
    if (sortConfigs[platform]?.key !== columnKey) return null;
    return sortConfigs[platform]?.direction === "asc" ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  const renderTableForPlatform = (platform) => {
    const Component = COMPONENT_ROWS[platform];
    if (!Component) return null;
    const sortConfig = sortConfigs[platform] || { key: null, direction: null };
    const conversionActions =
      campaign?.platformsData?.[platform]?.conversionActions;
    const platformStatistics = statistics?.[platform] || {};
    const metricsToShow = getMetricsToShow({
      platform,
      multipleConversionsKeys: collectMultipleConversionKeys(
        platformStatistics,
        platform,
        conversionActions
      ),
      actionMetricsKeys: getActionMetricKeys(platform),
    });
    const columns = [
      { key: "platform", label: COLUMN_NAME_PLATFORM },
      ...metricsToShow.map((key) => ({ key, sortable: true })),
    ];
    const stats = platformStatistics.campaign
      ? [platformStatistics.campaign]
      : [];
    const sortedStats = [...stats].sort((a, b) => {
      if (!sortConfig.key) return 0;
      const key = sortConfig.key;
      const comparison = (a[key] || 0) - (b[key] || 0);
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
    const visibility = columnVisibility[platform] || {};
    const isInitialized = metricsInitialized[platform];

    const availableColumns = columns;
    const availableMetrics = metricsToShow;
    const getMetricLabel = (metricKey) =>
      getCampaignMetricLabel(metricKey, platform, t);

    const visibleColumns = [
      ...(availableColumns.find((col) => col.key === "platform")
        ? [availableColumns.find((col) => col.key === "platform")]
        : []),
      ...availableColumns.filter(
        (col) => col.key !== "platform" && visibility[col.key] !== false
      ),
    ].map((col) => ({
      ...col,
      width: getColumnWidth(platform, col.key),
    }));

    return (
      <Box
        key={platform}
        data-pdf-capture-id={`metrics-table-card-${platform}`}
        sx={{ mb: 3 }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Avatar
              data-hide-in-pdf-capture
              src={logoPlatform(platform)}
              variant="square"
              sx={{
                width: 20,
                height: 20,
                ".MuiAvatar-img": { objectFit: "contain" },
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t(platform)}
            </Typography>
          </Box>
          <MetricsVisibilitySelector
            availableMetrics={availableMetrics}
            metricsVisibility={visibility}
            setMetricsVisibility={(newVisibility) => {
              setColumnVisibility((prev) => ({
                ...prev,
                [platform]: newVisibility,
              }));
            }}
            getLabel={getMetricLabel}
            isFromStatisticsLevel={true}
            campaignID={campaign?.id}
            platform={platform}
            onInitializationComplete={() => {
              setMetricsInitialized((prev) => ({
                ...prev,
                [platform]: true,
              }));
            }}
          />
        </Stack>
        {!isInitialized ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 100,
              color: "text.secondary",
            }}
          >
            <Typography>{t(LOADING_CONFIGURATION_METRICS)}</Typography>
          </Box>
        ) : (
          <TableContainer
            hasPagination={false}
            hasMarginBottom={false}
            keys={1}
          >
            <TableHead>
              <TableRow sx={{ padding: 0 }}>
                <TableCell isHeader={true} sx={expanderColumnSx} />
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    isHeader={true}
                    isResizable={true}
                    onResize={(width) =>
                      setColumnWidth(platform, col.key, width)
                    }
                    sx={metricColumnSx(col.width)}
                  >
                    {col.key === "platform" ? (
                      t(col.label)
                    ) : col.sortable ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 1,
                          cursor: "pointer",
                          minWidth: 0,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                        onClick={() => handleSort(platform, col.key)}
                      >
                        {getMetricLabel(col.key)}
                        <SortIcon platform={platform} columnKey={col.key} />
                      </Box>
                    ) : (
                      getMetricLabel(col.key)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedStats.map((stat, index) => (
                <Component
                  key={platform}
                  index={index}
                  platformData={campaign?.platformsData?.[platform] || {}}
                  sortConfig={sortConfig}
                  columns={visibleColumns}
                  stat={stat}
                />
              ))}
            </TableBody>
          </TableContainer>
        )}
      </Box>
    );
  };

  return (
    <Box
      id="metrics-section"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
        pt: 3,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        {t(ADS_CAMPAIGNS_STATISTICS_SLIDE_MENU_METRICS)}
      </Typography>
      {platforms.map((platform) => renderTableForPlatform(platform))}
    </Box>
  );
};

export default TableStatistics;
